/**
 * Person detail. One page per credited person — all 607 of them (2026-08-09,
 * when `personMinHymns` went 3 → 1 and closed 619 dead credit links).
 *
 * ⚠️ 386 OF THESE PEOPLE ARE CREDITED ON EXACTLY ONE HYMN, so this template is
 * carrying the thin-content risk that the old >=3 gate used to avoid. What keeps a
 * one-hymn page substantive is that it is a NODE IN A GRAPH rather than a leaf:
 * the hymns list, the spellings the source uses, the sections those hymns sit in,
 * and — the part that does the work — everyone else credited on the same hymns.
 * A visitor who lands on a harmonizer of one hymn can walk to its composer, its
 * author, and its season without going back to the index.
 *
 * Remove the collaborator section and these pages become 386 near-duplicates.
 *
 * `variants` is rendered rather than hidden. The source credits the same person
 * inconsistently — qualifiers, stray whitespace, and outright year typos (Bach as
 * "1685-1759", Hassler as "1564-1512") — and the model merges them by slug base.
 * Merging is right; merging SILENTLY is not, because an over-merge would attribute
 * one musician's work to another. Showing the spellings makes the merge auditable
 * by the people best placed to catch an error.
 */
import { Link, useParams } from 'react-router-dom'

import { HYMNAL_1982, index } from '@/data/hymnal'
import { creditedAcross, rolesSentence } from '@/lib/hymnal'
import { Crumb, HymnList, SectionTitle } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'

export default function PersonPage() {
  const { slug } = useParams<{ slug: string }>()
  const person = slug ? index.personBySlug.get(slug) : undefined

  if (!person) {
    return (
      <NotFound
        title="No such contributor"
        detail="Nobody by that name is credited in The Hymnal 1982."
        backTo={`/hymnal/${HYMNAL_1982.id}`}
        backLabel="Browse The Hymnal 1982"
      />
    )
  }

  const otherSpellings = person.variants.filter((v) => v !== person.name)

  const hymns = person.hymns
    .map((ref) => index.hymnBySlug.get(ref.slug))
    .filter((h): h is NonNullable<typeof h> => Boolean(h))

  // Sections this person's hymns sit in, in book order — where in the church year
  // their work is actually sung, which is the question a planner has.
  const sections = index.pages.sections.filter((s) => hymns.some((h) => h.sectionSlug === s.slug))

  // Everyone else credited on the same hymns. This is what makes a one-hymn page
  // worth having: a harmonizer links to that hymn's composer and author.
  const collaborators = [...creditedAcross(index, hymns).values()]
    .filter((c) => c.slug !== person.slug)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 16)

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={person.name}
        description={`${person.name} in The Hymnal 1982 — ${rolesSentence(person.roles).toLowerCase()} on ${person.hymns.length} hymns.`}
        path={`/hymnal/${HYMNAL_1982.id}/person/${person.slug}`}
        schema={{ '@context': 'https://schema.org', '@type': 'Person', name: person.name }}
      />
      <Crumb label={person.name} />

      <header className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-display text-foreground mb-2">{person.name}</h1>
        <p className="text-muted-foreground">
          {rolesSentence(person.roles)} · {person.hymns.length} hymns
        </p>
      </header>

      <section className="mb-8">
        <SectionTitle>Hymns</SectionTitle>
        <HymnList hymns={person.hymns} />
      </section>

      {sections.length > 0 && (
        <section className="mb-8">
          <SectionTitle>Where in the hymnal</SectionTitle>
          <ul className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/hymnal/${HYMNAL_1982.id}/section/${s.slug}`}
                  className="inline-block rounded border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-border-strong hover:text-foreground"
                >
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {collaborators.length > 0 && (
        <section className="mb-8">
          <SectionTitle>Credited alongside</SectionTitle>
          <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
            {collaborators.map((c) => (
              <li key={c.slug} className="text-sm">
                <Link
                  to={`/hymnal/${HYMNAL_1982.id}/person/${c.slug}`}
                  className="text-muted-foreground underline decoration-border underline-offset-4 hover:text-foreground hover:decoration-foreground"
                >
                  {c.name}
                </Link>
                {c.count > 1 && <span className="ml-1 text-xs text-muted-foreground">{c.count}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {otherSpellings.length > 0 && (
        <section className="mt-10 border-t border-border pt-5">
          <SectionTitle>Also credited as</SectionTitle>
          <ul className="text-sm text-muted-foreground space-y-1">
            {otherSpellings.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            The Hymnal 1982 credits some contributors inconsistently. These spellings are treated as one person here.
          </p>
        </section>
      )}
    </article>
  )
}
